import { useAuth } from "@/hooks/auth";
import { incrementViewCount } from "@/services/profiles/engagement/incrementViewCount";
import { ProfileData } from "@/v2/hooks/profiles/useProfiles";
import { requestAbout } from "@/v2/services/profiles/requestAbout";
import { requestPortfolio } from "@/v2/services/profiles/requestPortfolio";
import { requestExperience } from "@/v2/services/profiles/requestExperience";
import { useState } from "react";
import { hireMeRequest } from "@/v2/services/profiles/hireMeRequest";
import { HireMeFormData } from "@/components/profile/HireMeModal";

/**
 * Gets or creates a tracking identifier for analytics
 * - For authenticated users: uses their user ID
 * - For anonymous users: generates and persists a session ID
 */
const getTrackingId = (userId?: string): string => {
  // If user is authenticated, use their ID for tracking
  if (userId) {
    return userId;
  }

  // For anonymous users, get or create a session ID
  try {
    let sessionId = localStorage.getItem("userSessionId");

    if (!sessionId) {
      sessionId = crypto.randomUUID();
      localStorage.setItem("userSessionId", sessionId);
    }

    return sessionId;
  } catch (error) {
    // Fallback if localStorage is not available (e.g., private browsing)
    return crypto.randomUUID();
  }
};

/**
 * Hook for tracking profile interactions
 */
export const useProfileTracking = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState<{
    about: boolean;
    portfolio: boolean;
    experience: boolean;
    hireMe: boolean;
  }>({
    about: false,
    portfolio: false,
    experience: false,
    hireMe: false,
  });

  const trackProfileView = async (profile: ProfileData) => {
    // Don't track views of user's own profile
    if (profile.id === user?.id) {
      localStorage.setItem("userSessionId", user?.id);
      return;
    }

    const trackingId = getTrackingId(user?.id);
    try {
      const response = await incrementViewCount(
        profile.id,
        trackingId,
        user?.id
      );
      return response;
    } catch (err) {
      console.error("Failed to track profile view:", err);
      return {
        error: err,
      };
    }
  };
  const handleAboutRequest = async (profile: ProfileData) => {
    setLoading({ ...loading, about: true });
    if (profile.id === user?.id) {
      localStorage.setItem("userSessionId", user?.id);
    }
    const trackingId = getTrackingId(user?.id);
    try {
      const response = await requestAbout(profile.id, trackingId, user?.id);
      return response;
    } catch (err) {
      console.error("Failed to request about:", err);
      return {
        error: err,
      };
    } finally {
      setLoading({ ...loading, about: false });
    }
  };
  const handlePortfolioRequest = async (profile: ProfileData) => {
    setLoading({ ...loading, portfolio: true });
    if (profile.id === user?.id) {
      localStorage.setItem("userSessionId", user?.id);
    }
    const trackingId = getTrackingId(user?.id);
    try {
      const response = await requestPortfolio(profile.id, trackingId, user?.id);
      return response;
    } catch (err) {
      console.error("Failed to request portfolio:", err);
      return {
        error: err,
      };
    } finally {
      setLoading({ ...loading, portfolio: false });
    }
  };
  const handleExperienceRequest = async (profile: ProfileData) => {
    setLoading({ ...loading, experience: true });
    if (profile.id === user?.id) {
      localStorage.setItem("userSessionId", user?.id);
    }
    const trackingId = getTrackingId(user?.id);
    try {
      const response = await requestExperience(
        profile.id,
        trackingId,
        user?.id
      );
      return response;
    } catch (err) {
      console.error("Failed to request experience:", err);
      return {
        error: err,
      };
    } finally {
      setLoading({ ...loading, experience: false });
    }
  };
  const handleHireMeRequest = async (
    profile: ProfileData,
    formData: HireMeFormData
  ) => {
    setLoading({ ...loading, hireMe: true });
    if (profile.id === user?.id) {
      localStorage.setItem("userSessionId", user?.id);
    }
    const trackingId = getTrackingId(user?.id);
    try {
      const response = await hireMeRequest(
        profile.id,
        profile.username,
        trackingId,
        formData.name,
        formData.email,
        formData.projectDescription,
        parseFloat(formData.budget),
        formData.currency,
        formData.deadline
      );
      return response;
    } catch (err) {
      console.error("Failed to request hire me:", err);
      return {
        error: err,
      };
    } finally {
      setLoading({ ...loading, hireMe: false });
    }
  };

  const trackProfileLike = (profileId: string) => {
    // Add tracking logic here if needed
  };

  return {
    trackProfileView,
    trackProfileLike,
    handleAboutRequest,
    handlePortfolioRequest,
    handleExperienceRequest,
    handleHireMeRequest,
    loading,
  };
};
